import React, { useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Popover, Drawer, Table, Space, Modal, Typography } from 'antd';
import { BorderOutlined, CheckSquareOutlined, EditOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import PortfolioForm from "../../PortfolioForm/PortfolioForm";
import PortfolioPageForm from "../../PortfolioPages/PortfolioPageForm";
import { selectPortfolioById } from "../../../../store/slices/portfolios.slice";
import PortfoliosToolbar from "./PortfoliosToolbar";
import useTableColumns from "./tableConfig";
import NavTrail from "../../../../layouts/NavTrail";
import _ from 'lodash';
import SelectedPortfoliosController from "./SelectedPortfoliosController";
import usePortfoliosCollection from "../usePortfoliosCollection";
import usePortfolioGroupActions from "../../usePortfolioGroupActions";
import {useTickerSearchIndex} from "../../../../modules/Search/useTickerSearchIndex";
import ButtonGroup from "@mui/material/ButtonGroup";
import Button from "@mui/material/Button";
import {QueueRounded, AddRounded} from "@mui/icons-material";
import {Plus, List, ArrowUpRight} from 'lucide-react'
import {Link} from "react-router-dom";
import PortfolioGroups from "../../PortfolioGroups";

const PortfoliosTable = ({ portfolios, prefix }) => {

  const [view, setView] = useState('table');
  const [editingPortfolioId, setEditingPortfolioId] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [newPageDrawerVisible, setNewPageDrawerVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState({
        b: false,
        s: true,
        m: true,
  });
  const [showGroups, setShowGroups] = useState(false);

  const portfolioToEdit = useSelector((state) => selectPortfolioById(state, editingPortfolioId));
  const { toggleFavorite, handleEdit, handleUpdatePortfolioDetails } = usePortfoliosCollection();
  const { handleDelete } = usePortfolioGroupActions();

  const uniqueBenchmarkSymbols = useMemo(() => {
    return [...new Set(portfolios.map(p => p?.benchmark).filter(Boolean))];
  }, [portfolios]);
  const { tickers, loading } = useTickerSearchIndex(uniqueBenchmarkSymbols);

  // Filter logic
  const filterPortfolio = useCallback((portfolio) => {
    if (searchTerm && !portfolio.name.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }

    if (activeFilters.b || activeFilters.s || activeFilters.m || activeFilters.mm) {
      return (
        (activeFilters?.b && portfolio.type === 'b') ||
        (activeFilters?.s && portfolio.type === 's') ||
        (activeFilters?.m && portfolio.type === 'm') ||
        (activeFilters?.mm && portfolio.type === 'mm')
      );
    }
    return true;
  }, [searchTerm, activeFilters]);

  const sortPortfolios = useCallback((a, b) => {
        const aStarred = _.get(a, '_user.in_use', false) ? 1 : 0;
        const bStarred = _.get(b, '_user.in_use', false) ? 1 : 0;
        return bStarred - aStarred || a?.name.localeCompare(b?.name);
    }, []);

    // Update memoized portfolios
   const filteredPortfolios = useMemo(() => {
        if (!portfolios) return [];
        return [...portfolios]
            .filter(filterPortfolio)
            .sort(sortPortfolios);
    }, [portfolios, filterPortfolio, sortPortfolios]);


    const onEdit = useCallback((portfolioId) => {
        handleEdit(portfolioId).then((result) => {
          if (result.type === 'EDIT_SUCCESS') {
            setEditingPortfolioId(result.portfolioId);
            setView('edit');
          }
        });
      }, [handleEdit]);

    const onDelete = useCallback(() => {
        handleDelete(selectedKeys).then((success) => {
          if (success) {
            setSelectedKeys([]);
          }
        });
      }, [selectedKeys, handleDelete]);

    const columns = useTableColumns({
        onEdit,
        toggleFavorite,
        benchmarkTickers: tickers,
        setActiveFilters
    });

  const handleFilter = useCallback(({ searchTerm: newSearchTerm, filters }) => {
    setSearchTerm(newSearchTerm);
    setActiveFilters(filters);
  }, []);

  console.log(portfolios);


    const trailItems = useMemo(() => {
        const items = [{
            label: "Portfolios",
            path: '/portfolios',
            onClick: () => setView('table')
        }];

        if (view === 'edit' && portfolioToEdit) {
            items.push({
                label: (
                    <Typography.Text>
                        <Typography.Text style={{ color: 'var(--red)', fontWeight: 400, marginRight: '4px' }}>
                            Edit
                        </Typography.Text>
                        {portfolioToEdit.name}
                    </Typography.Text>
                ),
                icon: <EditOutlined />
            });
        }

        return items;
    }, [portfolioToEdit]);

  const rowSelection = useMemo(() => {
    return {
      columnWidth: 40,
      selectedRowKeys: selectedKeys,
      onSelect: (record, selected) => {
        setSelectedKeys(prevKeys => {
          if (selected) {
            return [...prevKeys, record.id];
          } else {
            return prevKeys.filter(key => key !== record.id);
          }
        });
      },
      onSelectAll: (selected, selectedRows, changeRows) => {
          if (selected) {
            // Select all rows
            const allIds = filteredPortfolios.map(portfolio => portfolio.id);
            setSelectedKeys(allIds);
          } else {
            // Deselect all rows
            setSelectedKeys([]);
            }
          }

    };

  }, [selectedKeys]);


  if (view === 'edit' && portfolioToEdit) {
    return (
      <div style={{width: '100%'}}>
        <NavTrail items={trailItems} />
        <PortfolioForm
          portfolioId={editingPortfolioId}
          initialValues={portfolioToEdit}
          handleSubmit={handleUpdatePortfolioDetails}
          onClose={() => setView('table')}
        />
      </div>
    );
  }



  return (
    <div className={'h-full overflow-hidden'}>
        <div style={{padding: '0 24px'}} className={'sticky top-0 z-500'}>
            <NavTrail items={trailItems} extraRight={
                  <Space size={'large'}>
                         <Button variant={'text'} color={'secondary'} className={'pill'}>
                              <div onClick={()=>setShowGroups(true)} className={'flex flex-row'}>
                                  Manage Groups<sup><List size={16}/></sup>
                              </div>
                          </Button>
                          <Button variant={'text'} color={'secondary'} className={'pill'}>
                              <Link to={'/builders/portfolio'} className={'flex flex-row'}>
                                  Create Portfolio<sup><Plus size={16}/></sup>
                              </Link>
                          </Button>
                          <Button
                                  variant={'text'}
                                  className={'pill'}
                                  disabled={portfolios.length===0}
                                  color={'secondary'}
                          >
                              <Link to={'/portfolios/paths'} className={'flex flex-row'}>
                                  Portfolio Paths<sup><ArrowUpRight size={16}/></sup>
                              </Link>
                          </Button>
                          <Button
                                  variant={'text'}
                                  className={'pill'}
                                  disabled={portfolios.length===0}
                                  color={'secondary'}
                          >
                             <Link to={'/signals'} className={'flex flex-row'}>
                                  Manage Signals<sup><ArrowUpRight size={16}/></sup>
                              </Link>
                          </Button>
                      </Space>
              }
            />
        </div>
      <div style={{ padding: '24px 48px 24px 36px' }}>
        <PortfoliosToolbar
          onFilter={handleFilter}
          portfolios={portfolios}
        />
        {selectedKeys.length > 0 && (
            <SelectedPortfoliosController selectedKeys={selectedKeys}
                                          onDelete={onDelete} />
        )}

        <Table
          dataSource={filteredPortfolios}
          columns={columns}
          className="nowrap-headers"
          rowKey="id"
          pagination={{ pageSize: 100 }}
          style={{ fontSize: '11px' }}
          rowSelection={rowSelection}
          scroll={{ y: 800, x: 'max-content' }}
        />
      </div>

      <Drawer
        title="Add New Portfolio Page"
        width={900}
        onClose={() => setNewPageDrawerVisible(false)}
        visible={newPageDrawerVisible}
        bodyStyle={{ paddingBottom: 80 }}
      >
        <PortfolioPageForm
          initialValues={{ portfolio_ids: [...selectedKeys] }}
        />
      </Drawer>
      <Modal open={showGroups}
             onCancel={()=>setShowGroups(false)}
             onOk={()=>setShowGroups(false)}
             width={'80vw'}
      >
            <PortfolioGroups/>
      </Modal>
    </div>
  );
};

export default PortfoliosTable;